import { FC, useMemo, useState } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useFeeVersions } from '../../hooks/useFees';
import { calculateSingleProductFees, calculateSingleProductFeesForPublication } from '../../services/feeService';
import { FeesEmulator, VersionOption } from './FeesEmulator';
import defaultPayload from '../../data/fees-emulator-default-payload.json';

const DEFAULT_PAYLOAD_JSON = JSON.stringify(defaultPayload, null, 2);

/**
 * Resolves a payload + a selected version into a fee response. Draft (versionId === null) hits the
 * public /fees/single endpoint with useUnpublishedConfiguration: true; any published version hits
 * the internal /fees/emulator/single endpoint pinned to that version id.
 */
const runForVersion = async (parsedPayload: any, versionId: number | null) => {
  if (versionId === null) {
    return calculateSingleProductFees({
      ...parsedPayload,
      settings: {
        ...(parsedPayload?.settings ?? {}),
        useUnpublishedConfiguration: true,
        feeRulesVersionId: undefined,
      },
    });
  }
  return calculateSingleProductFeesForPublication(parsedPayload, versionId);
};

const stringifyResponse = (resp: unknown): string => {
  try { return JSON.stringify(resp, null, 2); } catch { return ''; }
};

export const FeesEmulatorContainer: FC = () => {
  const { versionsQuery } = useFeeVersions();

  const [payload, setPayload] = useState(DEFAULT_PAYLOAD_JSON);
  const [versionAId, setVersionAId] = useState<number | null>(null); // Draft default per ticket
  const [versionBId, setVersionBId] = useState<number | null>(null); // unset until user picks

  const [responseA, setResponseA] = useState<string>('');
  const [responseB, setResponseB] = useState<string>('');
  const [errorA, setErrorA] = useState<string | null>(null);
  const [errorB, setErrorB] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [hasUserSelectedB, setHasUserSelectedB] = useState(false);

  const versions = versionsQuery.data ?? [];
  const versionOptions = useMemo<VersionOption[]>(() => {
    const options = [...versions]
      .sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0))
      .map(v => ({
        label: `${v.versionNumber}${v.description ? ` - ${v.description}` : ''}`,
        value: v.feeRulesVersionId,
      }));
    return [{ label: 'Draft', value: null }, ...options];
  }, [versions]);

  const parseResult = useMemo(() => {
    try {
      return { ok: true as const, value: JSON.parse(payload) };
    } catch (e: any) {
      return { ok: false as const, error: e?.message ?? 'Invalid JSON' };
    }
  }, [payload]);

  const canRun = parseResult.ok && hasUserSelectedB;

  const handleRun = async () => {
    if (!parseResult.ok) return;
    setIsRunning(true);
    setErrorA(null);
    setErrorB(null);
    setLookupError(null);

    // Dedupe: when both panes target the same publication, fire one request and reuse the result.
    if (versionAId === versionBId) {
      const [res] = await Promise.allSettled([runForVersion(parseResult.value, versionAId)]);
      if (res.status === 'fulfilled') {
        const json = stringifyResponse(res.value);
        setResponseA(json);
        setResponseB(json);
      } else {
        setResponseA('');
        setResponseB('');
        const msg = res.reason?.message ?? 'Request failed';
        setErrorA(msg);
        setErrorB(msg);
      }
      setIsRunning(false);
      return;
    }

    const [resA, resB] = await Promise.allSettled([
      runForVersion(parseResult.value, versionAId),
      runForVersion(parseResult.value, versionBId),
    ]);

    if (resA.status === 'fulfilled') setResponseA(stringifyResponse(resA.value));
    else { setResponseA(''); setErrorA(resA.reason?.message ?? 'Request failed'); }

    if (resB.status === 'fulfilled') setResponseB(stringifyResponse(resB.value));
    else { setResponseB(''); setErrorB(resB.reason?.message ?? 'Request failed'); }

    setIsRunning(false);
  };

  if (versionsQuery.isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <FeesEmulator
      payload={payload}
      onPayloadChange={setPayload}
      parseError={parseResult.ok ? null : parseResult.error}
      versionOptions={versionOptions}
      versionAId={versionAId}
      versionBId={versionBId}
      onVersionAChange={setVersionAId}
      onVersionBChange={id => { setVersionBId(id); setHasUserSelectedB(true); }}
      responseAJson={responseA}
      responseBJson={responseB}
      errorA={errorA ?? lookupError}
      errorB={errorB}
      isRunning={isRunning}
      canRun={canRun}
      onRun={handleRun}
      onLookupLoad={json => { setPayload(json); setLookupError(null); }}
      onLookupError={msg => setLookupError(msg)}
    />
  );
};

export default FeesEmulatorContainer;
